/* WHO DO I CALL FIRST, AND WHAT DO I SAY?
 *
 * Brian: "build the best tool for my team."
 *
 * The Cheer space now holds 519 gyms, each carrying a competitor's activity
 * tier, the price their families pay, and a championship footprint. That is a
 * lot of true facts and NOT YET A TOOL. A rep opening a board of 519 rows sorted
 * by name has been handed a spreadsheet, not a day's work.
 *
 * This turns the facts into an ORDER and a REASON. It is the difference between
 * "here is everything we know" and "call these nine, and here is your opening
 * line for each".
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠️ EVERY POINT SCORED MUST NAME ITS EVIDENCE.
 *
 * A score with no visible reason is a horoscope: the rep either trusts it
 * blindly or ignores it, and both are bad. So `signals` is the real output and
 * `score` is only the sort key. Each signal carries what we observed, why it
 * matters, and — where one exists — the receipt.
 *
 * ⚠️ AND IT NEVER INVENTS A REASON. A gym with nothing recorded scores ZERO and
 * says "no signal yet" rather than being ranked on a guess. Padding a weak row
 * into a confident-looking one is how a rep burns a call and stops believing
 * the board — which costs more than the row was ever worth.
 *
 * Pure. No React, no fetch, no imports.
 */

/* Ring price at or above this reads as a premium programme. The ACTIVE median
 * on the competitor's storefront is $400 and the spread is $215–$795, so this
 * sits meaningfully above the middle rather than at it. */
export const PREMIUM_PRICE = 420

/* A line item above this is a team package or deposit, not a ring — it must
 * never be read as what one athlete pays. */
export const PACKAGE_PRICE = 1000

export const BANDS = [
  { key: 'now', label: 'Call now', min: 60, tone: '#B4472A',
    hint: 'A live opening backed by evidence — work these first.' },
  { key: 'soon', label: 'Worth a call', min: 30, tone: '#B8862B',
    hint: 'A real signal, but a longer conversation.' },
  { key: 'watch', label: 'Watch', min: 1, tone: '#5A6B7A',
    hint: 'Something is here, but not enough to lead with.' },
  { key: 'none', label: 'No signal yet', min: 0, tone: '#8A8A8A',
    hint: 'Nothing recorded to open on. Enrichment, not outreach.' },
]

const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}
const S = (v) => String(v ?? '').trim()

/** The per-ring price, or null when the only figure is a team package. */
export function ringPrice(gym) {
  const p = num(gym?.incumbentRingPrice ?? gym?.sr_incumbent_ring_price)
  if (p == null || p <= 0) return null
  return p > PACKAGE_PRICE ? null : p
}

/**
 * Score a gym, and say why.
 *
 * @returns {{ score, band, label, tone, headline, signals: Array<{
 *   key, points, what, why, evidence
 * }> }}
 */
export function priorityFor(input) {
  /* ⚠️ A DEFAULT PARAMETER DOES NOT FIRE ON AN EXPLICIT `null` — only on
   * `undefined`. `priorityFor(null)` threw, and a record arriving as null from
   * a partial API read is entirely ordinary. Coalesce inside. */
  const gym = input || {}
  const signals = []
  const add = (key, points, what, why, evidence = '') =>
    signals.push({ key, points, what, why, evidence })

  const tier = S(gym.incumbentStatus || gym.sr_incumbent_status).toUpperCase()
  const price = ringPrice(gym)
  const titles = num(gym.titleCount) || 0
  const lastSeen = S(gym.incumbentLastSeen || gym.sr_incumbent_last_seen)
  const stage = S(gym.partnershipStage || gym.stageLabel).toLowerCase()
  const isPartner = /partner|jewelry live/.test(stage)
  const isSigned = /contract|signed|won/.test(stage)

  /* ── THE OPENING: the competitor has gone quiet ──────────────────────────
   * The strongest signal available, and the least obvious one. A gym whose
   * competitor storefront is empty or years cold is not "uncovered" — it is a
   * relationship that appears to have ENDED, which is the moment to arrive. */
  if (tier === 'EMPTY') {
    add('gone-empty', 40,
      'Their competitor storefront has no products at all',
      'Nobody can order from it. The relationship looks finished, which is the moment to be in the room.',
      gym.incumbentSource || gym.sr_incumbent_source || '')
  } else if (tier === 'STALE') {
    add('gone-stale', 40,
      `Competitor has published nothing new since ${lastSeen || 'over two years ago'}`,
      'Two years quiet usually means the relationship ended. Open as a fresh conversation, not a switch pitch.',
      gym.incumbentSource || gym.sr_incumbent_source || '')
  } else if (tier === 'COOLING') {
    add('cooling', 22,
      `Competitor quiet since ${lastSeen || '1–2 years ago'}`,
      'Could still be theirs, could have lapsed. Worth asking — never worth assuming.',
      gym.incumbentSource || gym.sr_incumbent_source || '')
  } else if (tier === 'ACTIVE') {
    /* ⚠️ NOT ZERO, AND NOT NEGATIVE. An actively-supplied gym is a harder
     * conversation, not a worthless one — it is a known buyer with a known
     * renewal. Scoring it below a gym we know nothing about would bury the
     * qualified lead beneath the blank one. */
    add('active-incumbent', 10,
      'Competitor is actively selling them',
      'A known buyer on a known cycle. Not a cold call — a timing question about when they next review.',
      gym.incumbentSource || gym.sr_incumbent_source || '')
  }

  /* ── THE QUALIFIER: they already pay for quality ─────────────────────────
   * Brian: "High priced rings most likely means open to legacy." A gym whose
   * families already pay $475 is not choosing on price, which is the ground SR
   * competes on. */
  if (price != null && price >= PREMIUM_PRICE) {
    add('premium', 25,
      `Their families already pay about $${Math.round(price)} a ring`,
      'Buying on quality, not price — which is the argument SR wins. Lead with the piece, not the quote.',
      '')
  } else if (price != null) {
    add('price-known', 6,
      `Ring price about $${Math.round(price)}`,
      'A known price point to pitch against.',
      '')
  }

  /* ── THE VOLUME: they are winning ────────────────────────────────────────
   * Titles are repeat ring business, every year, per team. */
  if (titles >= 5) {
    add('winning-big', 25,
      `${titles} championships across ${num(gym.teamCount) || 'several'} teams`,
      'A programme winning at this rate buys rings every season. The single strongest volume signal we hold.',
      '')
  } else if (titles > 0) {
    add('winning', 12,
      `${titles} championship${titles === 1 ? '' : 's'} recorded`,
      'Proven ring business, at a smaller scale.',
      '')
  }

  /* ── THE BRAKE: we already have them ─────────────────────────────────────
   * A partner does not belong at the top of a prospecting list. This is a
   * SUPPRESSION, not a negative signal — the gym is valuable, it just is not
   * who a rep should cold-call on a Monday. */
  if (isPartner) {
    add('already-partner', -70,
      'Already an SR partner',
      'Contract and jewelry programme live. This is account management, not prospecting.',
      '')
  } else if (isSigned) {
    add('already-signed', -40,
      'Contract already signed',
      'Mid-flight. The next move is launching the programme, not pitching it.',
      '')
  }

  const score = Math.max(0, signals.reduce((n, s) => n + s.points, 0))
  const band = BANDS.find((b) => score >= b.min) || BANDS[BANDS.length - 1]

  /* The one line a rep reads. Built from the highest-scoring POSITIVE signal,
   * so the headline is always something to act on rather than a caveat. */
  const lead = [...signals].filter((s) => s.points > 0).sort((a, b) => b.points - a.points)[0]
  const headline = lead ? lead.what : 'Nothing recorded to open on'

  return {
    score, band: band.key, label: band.label, tone: band.tone,
    headline, signals,
  }
}

/**
 * Order a whole board, and report the shape of it.
 *
 * ⚠️ RETURNS THE COUNTS TOO. "Here are your top 20" with no denominator cannot
 * be sanity-checked; "20 of 519, and 300 have no signal at all" tells a manager
 * something true about the state of the data as well as the pipeline.
 */
export function priorityBoard(gyms = []) {
  const scored = gyms.map((g) => ({ gym: g, priority: priorityFor(g) }))
  scored.sort((a, b) => b.priority.score - a.priority.score
    || String(a.gym.name || '').localeCompare(String(b.gym.name || '')))
  const counts = {}
  for (const s of scored) counts[s.priority.band] = (counts[s.priority.band] || 0) + 1
  return {
    rows: scored,
    counts,
    total: gyms.length,
    /* How much of the board we can actually say anything about. A low number
     * here is an ENRICHMENT problem, and naming it as one stops it being read
     * as "the team is not working the board". */
    actionable: (counts.now || 0) + (counts.soon || 0),
    noSignal: counts.none || 0,
  }
}
